import React from 'react';
import { Link } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import Footer from '../components/Footer';
import BottomNavBar from '../components/BottomNavBar';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold text-on-surface mb-3">{title}</h2>
    <div className="space-y-3 text-on-surface-variant leading-relaxed">{children}</div>
  </section>
);

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-surface pb-24 md:pb-0">
      <TopAppBar />
      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className="mb-8">
          <Link to="/" className="text-sm text-outline hover:text-on-surface flex items-center gap-1 mb-6">
            <span className="material-symbols-outlined text-[16px]">arrow_back</span>
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-on-surface mb-2">Terms of Service</h1>
          <p className="text-sm text-outline">Last updated: 4 June 2026 · Effective: 4 June 2026</p>
        </div>

        <div className="bg-secondary/10 rounded-xl p-5 mb-10 text-sm text-on-surface-variant">
          <p>
            These Terms of Service govern your use of the Buildon platform. By registering an account,
            you agree to these terms in full. If you do not agree, do not use the platform.
            These terms are governed by the laws of the Republic of South Africa.
          </p>
        </div>

        <Section title="1. About Buildon">
          <p>
            Buildon is an online marketplace that connects clients seeking building and construction
            services with independent contractors. Buildon is not a party to any agreement between
            a client and a contractor - we provide the platform only.
          </p>
          <p>
            Buildon does not employ contractors, guarantee the quality of any work performed, or
            accept liability for disputes between clients and contractors.
          </p>
        </Section>

        <Section title="2. Eligibility">
          <ul className="list-disc pl-6 space-y-2">
            <li>You must be at least 18 years old to register.</li>
            <li>
              If you register on behalf of a company or other legal entity, you confirm that you have
              authority to bind that entity to these terms.
            </li>
            <li>
              You may not register if you have previously been suspended or removed from Buildon.
            </li>
          </ul>
        </Section>

        <Section title="3. Your account">
          <ul className="list-disc pl-6 space-y-2">
            <li>You are responsible for keeping your account credentials secure.</li>
            <li>
              You must provide accurate and complete information when registering and keep your profile
              up to date.
            </li>
            <li>You may not share your account with any other person.</li>
            <li>
              You are responsible for all activity that occurs under your account. Notify us immediately
              at support@buildon.co.za if you suspect unauthorised access.
            </li>
          </ul>
        </Section>

        <Section title="4. Clients">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Clients may post jobs describing building or construction work they require. Job posts must
              be accurate, lawful, and in good faith.
            </li>
            <li>
              Clients may contact contractors through the platform messaging system. Communications must
              remain professional and lawful.
            </li>
            <li>
              Clients confirm job completion only when work is genuinely complete. False confirmation is
              a breach of these terms.
            </li>
            <li>
              Reviews submitted by clients must be honest, based on real experience, and must not contain
              defamatory, discriminatory, or misleading content.
            </li>
          </ul>
        </Section>

        <Section title="5. Contractors">
          <ul className="list-disc pl-6 space-y-2">
            <li>
              Contractors must hold all licences, registrations, and insurance required by South African
              law to perform the services they advertise. Uploading a credential document is a declaration
              that it is genuine and current.
            </li>
            <li>
              Contractors are solely responsible for the quality, safety, and legality of their work.
            </li>
            <li>
              Contractors may not misrepresent their qualifications, experience, credentials, or the
              nature of services they offer.
            </li>
            <li>
              Contractors confirm job completion only when the agreed scope of work is genuinely complete.
            </li>
          </ul>
        </Section>

        <Section title="6. Prohibited conduct">
          <p>You may not use Buildon to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Post false, misleading, or fraudulent content.</li>
            <li>Harass, threaten, or discriminate against other users.</li>
            <li>Solicit or accept payment outside the agreed job process to avoid platform records.</li>
            <li>Reverse-engineer, scrape, or otherwise extract data from the platform.</li>
            <li>Upload malware, viruses, or other harmful code.</li>
            <li>Impersonate any person or entity.</li>
            <li>Violate any applicable South African law or regulation.</li>
          </ul>
          <p>
            Buildon reserves the right to suspend or permanently remove accounts that breach these
            terms, without notice where the breach is serious.
          </p>
        </Section>

        <Section title="7. Intellectual property">
          <p>
            Buildon and its licensors own all rights in the platform, including its design, code,
            trademarks, and content produced by Buildon. Nothing in these terms transfers any
            intellectual property rights to you.
          </p>
          <p>
            By uploading content to Buildon (profile text, photographs, job descriptions), you grant
            Buildon a non-exclusive, royalty-free licence to display and use that content to operate
            the platform. You retain ownership of your content.
          </p>
        </Section>

        <Section title="8. Limitation of liability">
          <p>
            To the maximum extent permitted by applicable law, Buildon is not liable for:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>The quality, safety, or legality of any work performed by a contractor.</li>
            <li>Disputes between clients and contractors.</li>
            <li>Loss of data, revenue, or profits arising from your use of the platform.</li>
            <li>
              Interruption, errors, or unavailability of the platform, provided we take reasonable
              steps to maintain it.
            </li>
          </ul>
          <p>
            Nothing in these terms limits liability for fraud, gross negligence, or any liability that
            cannot be excluded under South African law including the Consumer Protection Act 68 of 2008.
          </p>
        </Section>

        <Section title="9. Indemnity">
          <p>
            You indemnify Buildon against any claims, losses, or costs (including legal costs) arising
            from your breach of these terms, your use of the platform, or your interactions with other users.
          </p>
        </Section>

        <Section title="10. Privacy">
          <p>
            Your use of the platform is also governed by our{' '}
            <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>,
            which forms part of these terms.
          </p>
        </Section>

        <Section title="11. Changes to these terms">
          <p>
            We may update these terms from time to time. Material changes will be communicated via a
            notice on the platform or by email at least 14 days before they take effect. Continued use
            of the platform after that date constitutes acceptance of the updated terms.
          </p>
          <p>
            If you do not accept updated terms, you must stop using the platform and may request account
            deletion.
          </p>
        </Section>

        <Section title="12. Governing law and disputes">
          <p>
            These terms are governed by the laws of the Republic of South Africa. Any disputes arising
            from these terms or your use of the platform must be referred to the courts of South Africa,
            subject to any applicable alternative dispute resolution process.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            For questions about these terms, contact us at{' '}
            <strong>legal@buildon.co.za</strong> or write to our registered address.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-outline-variant text-sm text-outline">
          <p>
            View our{' '}
            <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </main>
      <Footer />
      <BottomNavBar />
    </div>
  );
};

export default TermsPage;
